<template>
  <div :class="[classObj, { 'is-fullscreen': isFullscreen }]" class="app-wrapper" :style="{ '--current-color': theme }">
    <div v-if="device === 'mobile' && sidebar.opened" class="drawer-bg" @click="handleClickOutside" />
    <sidebar v-if="!sidebar.hide" class="sidebar-container" v-show="!isFullscreen" />
    <div :class="{ hasTagsView: needTagsView, sidebarHide: sidebar.hide }" class="main-container">
      <div :class="{ 'fixed-header': fixedHeader }">
        <navbar @setLayout="setLayout" v-show="!isFullscreen" />
        <tags-view v-if="needTagsView" />
      </div>
      <div style="position: relative;">
        <div class="fullScreen el-input__wrapper" @click="toggleFullscreen">
          <el-icon>
            <FullScreen />
          </el-icon>
        </div>
        <app-main />
      </div>
      <settings ref="settingRef" />
    </div>
  </div>
</template>

<script setup>
import { useWindowSize } from '@vueuse/core'
import Sidebar from './components/Sidebar/index.vue'
import { AppMain, Navbar, Settings, TagsView } from './components'
import defaultSettings from '@/settings'

import useAppStore from '@/store/modules/app'
import useSettingsStore from '@/store/modules/settings'

const settingsStore = useSettingsStore()
const theme = computed(() => settingsStore.theme);
const sideTheme = computed(() => settingsStore.sideTheme);
const sidebar = computed(() => useAppStore().sidebar);
const device = computed(() => useAppStore().device);
const needTagsView = computed(() => settingsStore.tagsView);
const fixedHeader = computed(() => settingsStore.fixedHeader);
const isFullscreen = ref(false);

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
}

const classObj = computed(() => ({
  hideSidebar: !sidebar.value.opened,
  openSidebar: sidebar.value.opened,
  withoutAnimation: sidebar.value.withoutAnimation,
  mobile: device.value === 'mobile'
}))

const { width, height } = useWindowSize();
const WIDTH = 992; // refer to Bootstrap's responsive design

watch(() => device.value, () => {
  if (device.value === 'mobile' && sidebar.value.opened) {
    useAppStore().closeSideBar({ withoutAnimation: false })
  }
})

watchEffect(() => {
  if (width.value - 1 < WIDTH) {
    useAppStore().toggleDevice('mobile')
    useAppStore().closeSideBar({ withoutAnimation: true })
  } else {
    useAppStore().toggleDevice('desktop')
  }
})

function handleClickOutside() {
  useAppStore().closeSideBar({ withoutAnimation: false })
}

const settingRef = ref(null);
function setLayout() {
  settingRef.value.openSetting();
}

// 获取菜单背景颜色
const getMenuBackground = computed(() => {
  if (settingsStore.isDark) {
    return 'var(--sidebar-bg)';
  }
  switch (sideTheme.value) {
    case 'theme-dark':
      return variables.menuBg;
    case 'theme-light':
      return variables.menuLightBg;
    case 'theme-blue':
      return variables.menuBlueBg; // 假设 variables 中有 menuBlueBg 变量
    case 'theme-atropurpureus':
      return variables.menuAtropurpureusBg;
    default:
      return variables.menuBg;
  }
});

// 获取菜单文字颜色
const getMenuTextColor = computed(() => {
  if (settingsStore.isDark) {
    return 'var(--sidebar-text)';
  }
  switch (sideTheme.value) {
    case 'theme-dark':
      return variables.menuText;
    case 'theme-light':
      return variables.menuLightText;
    case 'theme-blue':
      return variables.menuBlueText; // 假设 variables 中有 menuBlueText 变量
    case 'theme-atropurpureus':
      return variables.menuAtropurpureusText;
    default:
      return variables.menuText;
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixin.scss";
@import "@/assets/styles/variables.module.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - #{$base-sidebar-width});
  transition: width 0.28s;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px);
}

.sidebarHide .fixed-header {
  width: 100%;
}

.mobile .fixed-header {
  width: 100%;
}

.fullScreen {
  width: 30px;
  height: 30px;
  position: absolute;
  right: 2px;
  top: -32px;
  z-index: 999;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
}

.fullScreen:hover {
  background-color: rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.1);
}

.is-fullscreen {
  min-height: 100vh !important;

  .main-container {
    margin-left: 0 !important;
    width: 100% !important;
  }

  .fixed-header {
    width: 100% !important;
    left: 0;
  }

  // 如果 sidebar 有 transition，也一并处理
  .sidebar-container {
    display: none;
  }
}
</style>